import { ButtonBase, styled } from '@mui/material';

export type PillVariant = 'solid' | 'dashed';

// Same trailing-fade idea as GlobalTabs' close-button treatment (styled.tsx TRAILING_FADE):
// blends the label into the hover-only ellipsis overlay instead of a hard clip.
const LABEL_FADE = 'linear-gradient(to right, #000 calc(100% - 1.25rem), transparent 100%)';

const PILL_TRANSITION_MS = 120;
const PILL_PADDING_Y = '0.125rem';
const PILL_PADDING_X = '0.5625rem';
// A round glyph reads as further from the edge than its box is, so a pill that leads with
// an icon tightens that side to stay optically centred; a text-only pill matches both sides.
const PILL_PADDING_X_BEFORE_ICON = '0.375rem';
const PILL_FONT_SIZE = '0.71875rem';
const PILL_LINE_HEIGHT = 1.3;
// Half a one-line pill's height: a wrapped pill keeps the one-line pill's ends exactly and
// grows into a rounded rectangle, instead of a capsule with ever-larger round ends.
const PILL_WRAPPED_RADIUS = `calc((${PILL_FONT_SIZE} * ${PILL_LINE_HEIGHT} + 2 * ${PILL_PADDING_Y} + 2px) / 2)`;

export const PillRoot = styled(ButtonBase, {
  shouldForwardProp: (prop) =>
    prop !== 'variant' &&
    prop !== 'hasOverlay' &&
    prop !== 'hasAction' &&
    prop !== 'hasIcon' &&
    prop !== 'wrapLabel',
})<{
  variant: PillVariant;
  hasOverlay: boolean;
  hasAction: boolean;
  hasIcon: boolean;
  wrapLabel: boolean;
}>(({ theme, variant, hasOverlay, hasAction, hasIcon, wrapLabel }) => {
  const { sys } = theme.palette.custom.OV;
  const isDashed = variant === 'dashed';

  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3125rem',
    borderRadius: wrapLabel ? PILL_WRAPPED_RADIUS : 999,
    padding: `${PILL_PADDING_Y} ${PILL_PADDING_X} ${PILL_PADDING_Y} ${
      hasIcon ? PILL_PADDING_X_BEFORE_ICON : PILL_PADDING_X
    }`,
    fontSize: PILL_FONT_SIZE,
    lineHeight: PILL_LINE_HEIGHT,
    border: `1px solid ${sys.outlineVariant}`,
    borderStyle: isDashed ? 'dashed' : 'solid',
    backgroundColor: isDashed ? 'transparent' : sys.surfaceContainerHigh,
    color: isDashed ? sys.onSurfaceVariant : sys.onBackground,
    maxWidth: '100%',
    minWidth: 0,
    cursor: hasAction ? 'pointer' : 'default',
    transition: theme.transitions.create('border-color', {
      duration: PILL_TRANSITION_MS,
    }),

    '&:hover, &.Mui-focusVisible': {
      borderColor: isDashed ? sys.secondary : sys.outline,
    },

    ...(wrapLabel && {
      // The native <button> UA style centers wrapped lines.
      textAlign: 'left',
      // Icon, trailing decoration and hover action sit on the first line, not the middle.
      alignItems: 'flex-start',
      '& .pill-label': {
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        maxWidth: 'none',
      },
      '& .pill-overlay': {
        top: '0.1875rem',
        transform: 'none',
      },
    }),

    ...(hasOverlay && {
      '&:hover .pill-label, &.Mui-focusVisible .pill-label': {
        WebkitMaskImage: LABEL_FADE,
        maskImage: LABEL_FADE,
      },
      '&:hover .pill-trailing, &.Mui-focusVisible .pill-trailing': {
        opacity: 0,
      },
      '&:hover .pill-overlay, &.Mui-focusVisible .pill-overlay': {
        opacity: 1,
        pointerEvents: 'auto',
      },
    }),
  };
});

export const PillLabel = styled('span')(({ theme }) => ({
  fontSize: 'inherit',
  lineHeight: 'inherit',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
  maxWidth: '11rem',
  transition: theme.transitions.create(['mask-image', '-webkit-mask-image'], {
    duration: PILL_TRANSITION_MS,
  }),
}));

export const PillTrailing = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  transition: theme.transitions.create('opacity', { duration: PILL_TRANSITION_MS }),
}));

// A span, not a Box: it lives inside the pill's own <button>, which only admits
// phrasing content.
export const PillOverlay = styled('span', {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: PillVariant }>(({ theme, variant }) => {
  const { sys } = theme.palette.custom.OV;

  return {
    position: 'absolute',
    right: '0.1875rem',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '0.9375rem',
    height: '0.9375rem',
    borderRadius: 999,
    display: 'grid',
    placeItems: 'center',
    backgroundColor:
      variant === 'dashed' ? sys.surfaceContainerHigh : sys.surfaceContainerHighest,
    color: sys.onSurfaceVariant,
    cursor: 'pointer',
    opacity: 0,
    pointerEvents: 'none',
    transition: theme.transitions.create('opacity', { duration: PILL_TRANSITION_MS }),
  };
});
